import { isRequestError } from 'utils/requestError';
import React, { useEffect, useState, useRef } from 'react';
import {
  useGetUserProfileQuery,
  useGetBalanceQuery,
  useGetAdminActionsQuery,
} from 'services';
import { useNavigate } from 'react-router-dom';
import { Image } from '../image';
import Breadcrumbs from 'components/Breadcrumbs';
import { getUserRoles, handleLogout as handleLogoutUtils } from 'utils/helpers';
import { LocalStorageKey } from 'configs/constants';
import { MainRouteKeys, AuthRouteKeys } from 'routes/enums';
import { useLogoutMutation } from 'services';
import toast from 'react-hot-toast';
import { Icon } from 'components/icon';
import { Roles } from 'configs/roles';
import { ThemeToggle } from '../ThemeToggle';

const Header = (): JSX.Element => {
  const { data } = useGetUserProfileQuery();
  const {
    data: userBalance,
    isFetching: isFetchingBalance,
    refetch: refetchBalance,
  } = useGetBalanceQuery();
  const { refetch: refetchAdminActions } = useGetAdminActionsQuery();
  const { mutateAsync: mutateLogout, isLoading: isLoggingOut } =
    useLogoutMutation();
  const navigate = useNavigate();
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpenMenu(false);
      }
    };

    // Handle ESC to close
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpenMenu(false);
      }
    };

    if (isOpenMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpenMenu]);

  useEffect(() => {
    if (data && getUserRoles().includes(Roles.ADMIN)) {
      refetchAdminActions().then((actions) => {
        if (actions.isError) {
          localStorage.removeItem(LocalStorageKey.ADMIN_ACTIONS);
        } else {
          localStorage.setItem(
            LocalStorageKey.ADMIN_ACTIONS,
            JSON.stringify(actions?.data?.adminActions || []),
          );
        }
      });
    } else if (data) {
      localStorage.removeItem(LocalStorageKey.ADMIN_ACTIONS);
    }
  }, [data, refetchAdminActions]);

  const currencyFormat = (num: any | unknown): any => {
    if (num == null) return '';
    const parts = num?.toString()?.split('.');
    return (
      parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') +
      (parts[1] ? ',' + parts[1] : '')
    );
  };

  const handleLogout = async () => {
    try {
      await mutateLogout();
      handleLogoutUtils();
      toast.success('Đăng xuất thành công');
      navigate(AuthRouteKeys.Login);
    } catch (error: unknown) {
      handleLogoutUtils();
      navigate(AuthRouteKeys.Login);
    }
  };

  const handleRefreshBalance = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    try {
      const result = await refetchBalance();

      if (result.isError && !isRequestError(result.error)) {
        toast.error('Không thể cập nhật số dư');
      }
    } catch (error: unknown) {
      if (!isRequestError(error)) {
        toast.error('Không thể cập nhật số dư');
      }
    }
  };

  return (
    <div className='UserHead'>
      <div className='header-topbar'>
        {/* LEFT: Breadcrumb */}
        <div className='header-breadcrumb'>
          <Breadcrumbs />
        </div>

        {/* RIGHT: Actions */}
        <div className='header-actions'>
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Dropdown */}
          <div
            className={`user-dropdown ${isOpenMenu ? 'is-open' : ''}`}
            ref={dropdownRef}
            onClick={() => setIsOpenMenu(!isOpenMenu)}
          >
            <Image
              src={data?.avatar || '/assets/images/no-image.jpg'}
              type='avatar'
              className='user-avatar'
            />

            <div className='user-info'>
              <div className='user-name'>{data?.fullName || 'Người dùng'}</div>
              <div className='user-phone'>{data?.phoneNumber}</div>
              <div className='user-balance'>
                {currencyFormat(userBalance?.balance ?? 0)} đ
              </div>
            </div>

            <button
              type='button'
              className='balance-refresh-btn'
              onClick={handleRefreshBalance}
              disabled={isFetchingBalance}
              title='Cập nhật số dư'
            >
              <Icon.Reload />
            </button>

            <div className='dropdown-arrow'>
              <svg viewBox='0 0 10 6' fill='none'>
                <path
                  d='M1 1l4 4 4-4'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                />
              </svg>
            </div>

            {/* Dropdown Menu */}
            <div className='user-dropdown-menu'>
              <div className='dropdown-profile'>
                <div className='dp-name'>{data?.fullName}</div>
                {data?.phoneNumber && (
                  <div className='dp-phone'>{data?.phoneNumber}</div>
                )}
                {data?.email && <div className='dp-email'>{data?.email}</div>}
                <div
                  className='dp-balance'
                  onClick={() => navigate(MainRouteKeys.BalanceManage)}
                >
                  <span className='dp-balance-text'>
                    <svg width='13' height='13' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14v-4H7l5-8v4h4l-5 8z'
                        fill='#22c55e'
                      />
                    </svg>
                    Số dư: {currencyFormat(userBalance?.balance ?? 0)} đ
                  </span>
                  <button
                    type='button'
                    className='dp-balance-refresh'
                    onClick={handleRefreshBalance}
                    disabled={isFetchingBalance}
                    title='Cập nhật số dư'
                  >
                    <Icon.Reload />
                  </button>
                </div>
              </div>

              <div
                className='dropdown-item'
                onClick={() => navigate(MainRouteKeys.Personal)}
              >
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                  <circle
                    cx='12'
                    cy='7'
                    r='4'
                    stroke='currentColor'
                    strokeWidth='2'
                  />
                </svg>
                Hồ sơ cá nhân
              </div>

              <ThemeToggle variant='menu' className='dropdown-item' />

              <div className='dropdown-item logout-item' onClick={handleLogout}>
                {isLoggingOut ? (
                  <Icon.Loading />
                ) : (
                  <svg viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14 5-5-5-5m5 5H9'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
                Đăng xuất
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
